'use client';

import { useState } from 'react';
import { DocumentTypeCode } from '@/apiClient';
import { usePrivate } from '@/components/private';
import { useShell } from '@/providers';
import { Box, Button, DocumentImage, Skeleton, Typography } from '@/primitives';
import { OrganisationSelectorModal } from '@/primitives/UserAvatar/organisation-selector/OrganisationSelectorModal';
import {
  FOOTER_HEIGHT,
  MOBILE_FOOTER_HEIGHT,
  NAV_HEIGHT,
  SIDEBAR_WIDTH,
  SIDEBAR_WIDTH_COLLAPSED,
} from '@/configs/layout';
import noLogoSvg from '@/components/organisations/noLogo.svg';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import SkipPreviousIcon from '@mui/icons-material/SkipPrevious';
import ShellSidebarLinks from './ShellSidebarLinks';
import { blue, white } from '@/lib/colours';
import { rgba } from 'polished';

export default function ShellSidebar() {
  const { userOrganisations, selectedOrganisationId } = usePrivate();
  const { sidebarExpanded, setConfigMenuOpen, setSidebarExpanded } = useShell();

  const selectedOrganisation = userOrganisations?.find(
    (o) => o.organisationId === selectedOrganisationId,
  );

  const [organisationSelectorOpen, setOrganisationSelectorOpen] =
    useState(false);

  const handleToggleModal = () => {
    if (!userOrganisations?.length) return;
    setOrganisationSelectorOpen(true);
  };

  return (
    <Box
      component="nav"
      sx={{
        boxSizing: 'border-box',
        position: 'relative',
        padding: '16px 12px 12px',
        height: '100%',
        minHeight: {
          xs: `calc(100vh - ${NAV_HEIGHT}px - ${MOBILE_FOOTER_HEIGHT}px)`,
          md: `calc(100vh - ${NAV_HEIGHT}px - ${FOOTER_HEIGHT}px)`,
        },
        width: `${sidebarExpanded ? SIDEBAR_WIDTH : SIDEBAR_WIDTH_COLLAPSED}px`,
        background: blue[900],
        overflow: 'hidden',
        transition: 'width 0.1s ease-in-out',

        '@media print': {
          display: 'none',
        },
      }}
    >
      {!userOrganisations?.length && (
        <Skeleton
          sx={{
            mb: '20px',
            border: `1px solid ${white[900]}`,
            borderRadius: '4px',
            height: sidebarExpanded ? '66px' : '48px',
            transform: 'none',
          }}
        />
      )}
      {selectedOrganisation && (
        <Box
          onClick={handleToggleModal}
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            mb: '20px',
            border: `1px solid ${white[900]}`,
            borderRadius: '4px',
            px: sidebarExpanded ? '12px' : '8px',
            py: sidebarExpanded ? '16px' : '11px',
            height: sidebarExpanded ? '66px' : '48px',
            background: rgba(0, 0, 0, 0),
            cursor: 'pointer',

            '&:hover': {
              background: rgba(0, 0, 0, 0.2),
            },
          }}
        >
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '4px',
              height: sidebarExpanded ? 32 : 24,
              maxWidth: sidebarExpanded ? 36 : 27,
              background: white[900], // temp solution: org data needs a background color prop
            }}
          >
            <DocumentImage
              alt="Breed society logo"
              documentTypeCode={DocumentTypeCode.Logo}
              documentEntityId={
                selectedOrganisation?.organisation?.organisationEntityId || ''
              }
              fallbackImage={noLogoSvg}
              fill
              sx={{
                position: 'unset !important',
                borderRadius: '4px',
                height: 'auto !important',
                width: 'auto !important',
                maxHeight: '100%',
                maxWidth: '100%',
                objectFit: 'contain',
              }}
            />
          </Box>
          {sidebarExpanded && (
            <Typography
              sx={{
                ml: '12px',
                color: white[900],
                fontWeight: 400,
              }}
              variant="labellarge"
            >
              {selectedOrganisation?.organisation?.description}
            </Typography>
          )}
          <ExpandMoreIcon
            sx={{
              position: 'absolute',
              top: sidebarExpanded ? '21px' : '12px',
              right: sidebarExpanded ? '12px' : '2px',
              color: white[900],
            }}
          />
        </Box>
      )}

      <ShellSidebarLinks />

      <Button
        sx={{
          position: 'absolute',
          bottom: '16px',
          right: sidebarExpanded ? '16px' : '20px',
          p: '16px',
          borderRadius: '4px',
          height: '50px',
          width: '50px',
          minWidth: '50px',
          background: blue[200],
          color: blue[600],
        }}
        onClick={() => {
          setConfigMenuOpen(!sidebarExpanded);
          setSidebarExpanded(!sidebarExpanded);
        }}
      >
        {sidebarExpanded ? <SkipPreviousIcon /> : <SkipNextIcon />}
      </Button>

      <OrganisationSelectorModal
        open={organisationSelectorOpen}
        onClose={() => setOrganisationSelectorOpen(false)}
      />
    </Box>
  );
}
